import { Button, FloatingActionBar, Modal } from '@dev-spendesk/grapes';
import { type Filter } from '@spendesk/bookkeeping-core-types';
import * as NEA from '@spendesk/general-type-helpers/NonEmptyArray';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useNotifications } from 'modules/app/notifications';
import { trackPayableAllRequested } from 'modules/bookkeep/export/analytics';
import {
  useActivePayableFilter,
  PayableTable,
  PayableTableSkeleton,
  PayableTableEmptyResult,
} from 'modules/payable/components';
import { useGetPayableCount, useGetPayableList } from 'modules/payable/hooks';
import {
  useExportPayableReport,
  useExportPayableReportErrorMessage,
} from 'modules/payable-report/hooks/api';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import {
  type FilterWithOptionalField,
  withoutOptionalFields,
} from 'src/core/common/components/FilterBuilder';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { toCoreTypeFilter } from 'src/core/modules/payable/hooks/api/payable-filter/payableFilterTransformer';

import { useFiltersContext } from './PayablesFiltersContainer/hooks';
import { trackFiltersV2 } from '../../utils/track-filters';

type Props = {
  payableId: string;
  onClick(payableId: string): void;
};

export const PayableTableContainer = ({ payableId, onClick }: Props) => {
  const { state, updateUrlParams, actions } = useFiltersContext();
  const companyId = useCompanyId();
  const history = useHistory();

  const activeFilter = useActivePayableFilter({ variant: 'all-payables' });

  const payablesQueryState = useGetPayableList({
    filters: state,
    filtersV2: activeFilter?.filter,
    options: {
      onSuccess: () =>
        activeFilter?.filter ? undefined : updateUrlParams(state),
    },
  });

  const countPayablesQueryState = useGetPayableCount({
    filters: state,
    filtersV2: activeFilter?.filter,
  });

  useEffect(() => {
    trackFiltersV2(activeFilter, 'all-payables');
  }, [JSON.stringify(activeFilter)]);

  const queryStates = useQueryStates({
    states: {
      payables: payablesQueryState,
      totalPayables: countPayablesQueryState,
    },
    reshapeData: ({ payables, totalPayables }) => ({
      payables,
      totalPayables: totalPayables.totalCount,
    }),
  });

  return (
    <QuerySuspense
      queryState={queryStates}
      loading={<PayableTableSkeleton />}
      fallback={(error) => (
        <QueryError
          queryError={error}
          componentType="ErrorState"
          translations={{}}
        />
      )}
    >
      {({ payables, totalPayables }) => {
        if (totalPayables === 0) {
          return (
            <PayableTableEmptyResult
              onResetFilters={() => {
                actions.reset();
                history.push(
                  routeFor(routes.PAYABLES_ALL.path, {
                    company: companyId,
                  }),
                );
              }}
            />
          );
        }

        return (
          <PayableTable
            activePayableId={payableId}
            payables={payables}
            totalPayables={totalPayables}
            hasNextPage={payablesQueryState.hasNextPage}
            onFetchNextPage={payablesQueryState.fetchNextPage}
            onClick={onClick}
            renderActions={(props) => (
              <PayableFloatingActionBar
                {...props}
                filter={activeFilter?.filter}
              />
            )}
          />
        );
      }}
    </QuerySuspense>
  );
};

/**
 * Table actions
 */

const PayableFloatingActionBar = ({
  numberOfSelectedRows,
  areAllRowsSelected,
  unselectedRows,
  selectedRows,
  setSelectedRows,
  filter,
}: {
  numberOfSelectedRows: number;
  areAllRowsSelected: boolean;
  unselectedRows: string[];
  selectedRows: string[];
  setSelectedRows: (rows: string[]) => void;
  filter: FilterWithOptionalField | undefined;
}) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { state } = useFiltersContext();
  const [exportPayableReport] = useExportPayableReport();
  const exportPayableReportErrorMessage = useExportPayableReportErrorMessage();

  const filtersV2 = filtersV2ToApi({
    filter: toCoreTypeFilter(
      withoutOptionalFields(
        filter ?? {
          operator: 'and',
          subfilters: [],
        },
      ),
    ),
    payableIdsToFilter: !areAllRowsSelected
      ? NEA.fromArray(selectedRows)
      : undefined,
  });

  const exportPayables = async () => {
    trackPayableAllRequested();
    setIsModalOpen(true);
    try {
      await exportPayableReport({
        filtersV2,
        search: state.search,
        ids: !areAllRowsSelected ? selectedRows : undefined,
        excludeIds:
          areAllRowsSelected && unselectedRows.length > 0
            ? unselectedRows
            : undefined,
      });
    } catch (error) {
      const errorMessage = exportPayableReportErrorMessage(error);
      dangerNotif(errorMessage);
    }
  };

  if (numberOfSelectedRows === 0) {
    return null;
  }

  return (
    <>
      <FloatingActionBar
        data-testid="payable-list-actions"
        actions={[
          {
            text: t('payables.export.downloadSelection'),
            iconName: 'arrow-down-tray' as const,
            onClick: exportPayables,
            isDisabled: numberOfSelectedRows === 0,
          },
        ]}
      >
        {t('payables.export.payablesSelected', {
          count: numberOfSelectedRows,
        })}
      </FloatingActionBar>

      <Modal
        isOpen={isModalOpen}
        iconName="circle-information"
        iconVariant="info"
        title={t('wallet.statements.downloadModal.title')}
        actions={[
          <Button
            key="confirm"
            variant="primaryBrand"
            text={t('wallet.statements.downloadModal.hide')}
            onClick={() => {
              setIsModalOpen(false);
              setSelectedRows([]);
            }}
          />,
        ]}
      >
        <div>{t('wallet.statements.downloadModal.subtitle')}</div>
      </Modal>
    </>
  );
};

const filtersV2ToApi = ({
  filter,
  payableIdsToFilter,
}: {
  filter: Filter;
  payableIdsToFilter?: NEA.NonEmptyArray<string>;
}): Filter => {
  if (payableIdsToFilter) {
    return {
      field: 'id',
      operator: '=',
      value: payableIdsToFilter,
    };
  }

  return filter;
};
